/** @format */

import React, {
  ChangeEvent,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Button, Modal, Form, Alert } from "react-bootstrap";

import { ReactComponent as ExclamationCircle } from "bootstrap-icons/icons/exclamation-circle.svg";
import { ReactComponent as Upload } from "bootstrap-icons/icons/upload.svg";
import { Card, Resource, ResourceReference } from "../../resources/types";
import { useAppDispatch } from "../../../app/hooks";
import {
  resourcesApi,
  useUpdateResourceMutation,
  useUpdateResourceReferenceMutation,
} from "../../../app/services/resource/resourcesApi";
import { useParams } from "react-router-dom";
import { XMLParser } from "fast-xml-parser";
import {
  cardsApi,
  useSaveResourceCardsMutation,
} from "../../../app/services/resource/cardsApi";
import { Trans, useTranslation } from "react-i18next";
import { gatherResourceUpdates, ResourceUpdate, updateResource } from "./utils";
import BLSpinner from "../../../assets/animations/BLSpinner/BLSpinner";
import { getObjectDiff } from "../../../utils/objDiff";

interface HTMLFileInputElement extends HTMLInputElement {
  files: FileList;
}

type Props = {
  resource: Resource;
};

const UploadTranslated: React.FC<Props> = ({ resource }) => {
  const dispatch = useAppDispatch();
  const { t: _ } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { resourceId } = useParams();

  const [saveResource] = useUpdateResourceMutation();
  const [saveCards] = useSaveResourceCardsMutation();
  const [saveResourceReference] = useUpdateResourceReferenceMutation();

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  const [resourceRefExists, setResourceRefExists] = useState<boolean>();
  const [resourceRef, setResourceRef] = useState<ResourceReference>();
  const [codesMatch, setCodesMatch] = useState<boolean>();
  const [error, setError] = useState<string>();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [fileContent, setFileContent] = useState<Blob>();
  const [fileCode, setFileCode] = useState<string>();
  const [fileLocale, setFileLocale] = useState<"en" | "cy">();

  const getResourceReference = useCallback(async () => {
    if (fileLocale && fileCode) {
      await dispatch(
        resourcesApi.endpoints.getResourceReference.initiate({
          code: fileCode,
          locale: fileLocale,
        }),
      )
        .unwrap()
        .then((data) => {
          setResourceRef(data);
          setResourceRefExists(true);
        })
        .catch((error) => {
          if (error.status !== 404) {
            setError(_("Failed to fetch resource reference"));
          }
          setResourceRefExists(false);
        });
    }
  }, [fileLocale, fileCode, dispatch, _]);

  useEffect(() => {
    setCodesMatch(!!resourceId && !!fileCode && fileCode === resourceId);

    return () => {
      setCodesMatch(undefined);
    };
  }, [resourceId, fileCode, fileLocale]);

  useEffect(() => {
    getResourceReference();
  }, [getResourceReference]);

  const cleanup = () => {
    setResourceRefExists(undefined);
    setResourceRef(undefined);
    setError(undefined);
    setIsLoading(false);
    setCodesMatch(undefined);
    setFileContent(undefined);
    setFileCode(undefined);
    setFileLocale(undefined);
  };

  useEffect(() => {
    return () => cleanup();
  }, [showUploadModal]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    cleanup();

    if (!!e.target.files && e.target.files.length > 0) {
      const labels = e.target.files[0].name.split(".");

      let lang;
      if (labels.length > 1 && labels[labels.length - 1] === "xml") {
        labels.pop();
      }
      if (
        labels.length > 1 &&
        (labels[labels.length - 1] === "en" ||
          labels[labels.length - 1] === "cy")
      ) {
        lang = labels.pop() as "en" | "cy";
        setFileLocale(lang);
      }
      setFileCode(labels.join("."));
      setFileContent(e.target.files[0]);
    } else {
      setFileCode(undefined);
      setFileContent(undefined);
      setFileLocale(undefined);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!!resourceId && !!fileContent && !!fileLocale) {
      setIsLoading(true);

      const fileReader = new FileReader();

      fileReader.onload = async () => {
        // Parse file's XML to JSON
        const parser = new XMLParser();
        const output = parser.parse(fileReader.result as string);

        let resourceUpdates: ResourceUpdate[];

        try {
          // Convert JSON into item updates
          resourceUpdates = gatherResourceUpdates(output, fileLocale);
        } catch (error) {
          setError("Failed to upload translation");
          return;
        }

        // Split updates into updates to resource and updates to cards
        const _resourceUpdates = [...resourceUpdates].filter(
          (item) => !item.updateIn.includes("cards"),
        );
        const _cardUpdates = [...resourceUpdates].filter((item) =>
          item.updateIn.includes("cards"),
        );

        let _resource = { ...resource };

        // Loop through resource updates and make changes
        for (const i in _resourceUpdates) {
          const updateValue = _resourceUpdates[i].updateValue as any;
          let updateIn = _resourceUpdates[i].updateIn.reverse();

          _resource = updateResource(updateIn, updateValue, _resource);
        }

        // Remove parts of updateIn that don't affect path for card objects
        const toUpdate = _cardUpdates.map((update) => ({
          ...update,
          updateIn: update.updateIn.slice(6),
        }));

        // Get all cards for a resource
        let { data: _cards } = await dispatch(
          cardsApi.endpoints.getResourceCards.initiate({ resourceId }),
        );

        // Loop through card updates and make changes
        for (const i in toUpdate) {
          const updateValue = toUpdate[i].updateValue as any;
          let updateIn = toUpdate[i].updateIn.reverse();

          _cards = updateResource(updateIn, updateValue, _cards) as
            | Card[]
            | undefined;
        }

        // Save card updates
        if (_cards) {
          try {
            const saveCardsRes = await saveCards({
              resourceCardItems: _cards.map((card: Card) => ({
                ...card,
                Code: resourceId,
              })),
            });

            if ((saveCardsRes as any).error !== undefined) {
              setError("Failed to save cards");
              console.error("Failed to save cards");
              return;
            }
          } catch {
            setError("Failed to save cards");
            throw Error("Failed to save cards");
          }
        }

        // Save resource updates
        const resourceDiff = getObjectDiff(resource as any, _resource as any);
        if (Object.keys(resourceDiff).length > 0) {
          try {
            await saveResource({
              primaryKey: { PK: _resource.PK, SK: _resource.SK },
              diff: resourceDiff,
            });
          } catch {
            setError("Failed to save resource");
          }
        }

        if (resourceRefExists && resourceRef) {
          // UPDATE RESOURCE REFERENCE
          let title = _resource.Object?.resourceTitle[fileLocale];
          if (title && title !== resourceRef.Object.title) {
            await saveResourceReference({
              primaryKey: { PK: resourceRef.PK, SK: resourceRef.SK },
              refInformation: {
                locale: resourceRef.Locale,
                type: resourceRef.Type,
                code: resourceRef.Code,
              },
              changes: { Object: { title: title } },
            });
          }
        } else {
          // CREATE NEW RESOURCE REFERENCE
          console.error("resource ref does not exist");
        }

        setShowUploadModal(false);
      };

      fileReader.readAsText(fileContent);
    }
  };

  return (
    <>
      <Button onClick={() => setShowUploadModal(true)}>
        <Upload fill="#AEAEAE" /> {_("Upload Translated")}
      </Button>
      <Modal
        dialogClassName="upload-translation-modal"
        show={showUploadModal}
        onHide={() => {
          setError(undefined);
          setFileCode(undefined);
          setFileContent(undefined);
          setFileLocale(undefined);
          setResourceRefExists(undefined);
          setShowUploadModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          {_("Upload Translated Resource")}
        </Modal.Header>
        <Modal.Body>
          <Trans
            i18nKey="file format"
            components={{ span: <span className="text-editing" /> }}
          />
          <Form onSubmit={handleSubmit}>
            <Form.Control
              type="file"
              aria-label="file-input"
              id="upload-document"
              className="mt-2"
              onChange={(e: ChangeEvent<HTMLFileInputElement>) =>
                handleChange(e)
              }
              accept="application/xml"
              ref={fileInputRef}
            />
            <div className="mt-3">
              {resourceRefExists && codesMatch && (
                <Alert variant={`${fileLocale === "cy" ? "light" : "warning"}`}>
                  <div className="d-flex gap-2">
                    {fileLocale !== "cy" && (
                      <div className="mr-2">
                        <ExclamationCircle />
                      </div>
                    )}
                    <div>
                      <Trans
                        i18nKey="uploading file"
                        components={{ strong: <strong />, br: <br /> }}
                        values={{
                          langP:
                            fileLocale === "en"
                              ? "English"
                              : fileLocale === "cy"
                              ? "Welsh"
                              : `Unknown Language ${fileLocale}`,
                        }}
                      />
                    </div>
                  </div>
                </Alert>
              )}

              {fileCode && !codesMatch && fileLocale && (
                <Alert variant="danger">
                  <div className="d-flex gap-2">
                    <div className="mr-2">
                      <ExclamationCircle height={16} className="text-danger" />
                    </div>
                    <div>
                      <Trans
                        i18nKey="wrong resource name"
                        components={{ p: <p />, code: <code /> }}
                        values={{
                          fileCode: fileCode,
                          resourceId: resourceId,
                        }}
                      />
                    </div>
                  </div>
                </Alert>
              )}

              {fileCode && !fileLocale && (
                <Alert variant="danger">
                  <div className="d-flex gap-2">
                    <div className="mr-2">
                      <ExclamationCircle height={16} className="text-danger" />
                    </div>
                    <div>
                      <Trans
                        i18nKey="invalid locale"
                        components={{ p: <p />, code: <code /> }}
                      />
                    </div>
                  </div>
                </Alert>
              )}

              {!!error && (
                <Alert variant="danger">
                  <div className="d-flex gap-2">
                    <div className="mr-2">
                      <ExclamationCircle height={16} className="text-danger" />
                    </div>
                    <div>{_("Error: {{error}}", { error })}</div>
                  </div>
                </Alert>
              )}
            </div>
            <div className="mt-3 justify-content-end d-flex gap-2">
              <Button
                variant="default"
                onClick={() => setShowUploadModal(false)}
              >
                {_("Cancel")}
              </Button>
              <Button
                variant="editing"
                type="submit"
                disabled={
                  (fileLocale && fileCode && resourceRefExists === undefined) ||
                  !!error ||
                  !codesMatch ||
                  isLoading
                }
              >
                {!error &&
                (isLoading ||
                  (fileLocale &&
                    fileCode &&
                    resourceRefExists === undefined)) ? (
                  <BLSpinner />
                ) : (
                  _("Upload")
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UploadTranslated;
